import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Loader2, Receipt, Undo2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Modal } from '@/shared/ui/Modal';
import { Pestanas, type Pestana } from '@/shared/ui/Pestanas';
import { notificar } from '@/shared/ui/avisos';
import { FacturasPorOrigen } from '@/modules/facturacion/FacturasPorOrigen';
import type { Ciclo, Consolidacion } from './contrato';
import { AvisoDelPlazo } from './PlazoDeLaOrdenanza';
import { EmisionSupervisada } from './EmisionSupervisada';

type Vista = 'ciclos' | 'sire';

const VISTAS: ReadonlyArray<Pestana<Vista>> = [
  {
    clave: 'ciclos',
    rotulo: 'Ciclos de pesaje',
    descripcion: 'Lo que factura este sistema',
    icono: Receipt,
  },
  {
    clave: 'sire',
    rotulo: 'Cartera anterior',
    descripcion: 'Lo que quedó por cobrar',
    icono: History,
  },
];

export function PaginaFacturacionDeGeneradores() {
  const [vista, setVista] = useState<Vista>('ciclos');
  const [elegido, setElegido] = useState<number | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const clienteDeConsultas = useQueryClient();
  const { puede } = useSesion();

  const ciclos = useQuery({
    queryKey: ['generadores', 'ciclos'],
    queryFn: () => api.get<Ciclo[]>('/generadores/ciclos'),
  });

  const facturables = (ciclos.data ?? []).filter((c) => c.estado !== 'ABIERTO');
  const ciclo =
    facturables.find((c) => c.id === elegido) ??
    facturables.find((c) => c.estado === 'CERRADO') ??
    facturables.find((c) => c.estado === 'FACTURADO' && c.sinFacturar > 0) ??
    facturables[0];
  // Un ciclo facturado al que una nota de credito o una anulacion le solto pesajes: se vuelve a
  // facturar solo por esos.
  const refacturable = ciclo?.estado === 'FACTURADO' && ciclo.sinFacturar > 0;

  const consolidado = useQuery({
    queryKey: ['generadores', 'consolidado', ciclo?.id],
    enabled: ciclo?.estado === 'CERRADO',
    queryFn: () => api.get<Consolidacion>(`/generadores/ciclos/${ciclo!.id}/consolidado`),
  });

  const facturar = useMutation({
    mutationFn: () =>
      api.post<{ facturas: number; total: number }>(`/generadores/ciclos/${ciclo!.id}/facturar`),
    meta: { procesando: 'Generando las facturas…' },
    onSuccess: (r) => {
      notificar.exito(
        `${r.facturas} facturas · $${r.total.toFixed(2)}`,
        refacturable ? 'Lo liberado está refacturado' : 'Ciclo facturado',
      );
      setConfirmando(false);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['generadores'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['facturas'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO FACTURAR'),
  });

  const t = consolidado.data?.totales;
  const refrescar = () => void clienteDeConsultas.invalidateQueries({ queryKey: ['generadores'] });
  const conVistoBueno = ciclo?.vistoBuenoVigente === true;

  return (
    <>
      <EncabezadoDePagina
        icono={Receipt}
        titulo="Facturación de generadores"
        subtitulo="Una factura por generador y mes · Art. 9 literales b, c y d"
        acciones={
          <span className="flex flex-wrap items-center gap-2">
            {vista === 'ciclos' && facturables.length > 0 && (
              <select
                value={ciclo?.id ?? ''}
                onChange={(e) => setElegido(Number(e.target.value))}
                aria-label="Ciclo"
                className="h-10 rounded-lg border border-armazon-200 bg-white px-2 text-xs font-semibold uppercase text-armazon-700"
              >
                {facturables.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${enMes(c.periodo)} · ${c.estado}`}
                  </option>
                ))}
              </select>
            )}
            {vista === 'ciclos' &&
              (ciclo?.estado === 'CERRADO' || refacturable) &&
              puede('generadores.gestionar') && (
                <button
                  type="button"
                  onClick={() => setConfirmando(true)}
                  disabled={!conVistoBueno}
                  title={
                    conVistoBueno ? undefined : 'Falta el visto bueno de quien supervisa la emisión'
                  }
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {refacturable ? (
                    <Undo2 className="size-4" aria-hidden />
                  ) : (
                    <Receipt className="size-4" aria-hidden />
                  )}
                  {refacturable
                    ? `Refacturar lo liberado (${ciclo.sinFacturar})`
                    : `Facturar ${enMes(ciclo.periodo)}`}
                </button>
              )}
          </span>
        }
      />

      <Pestanas
        pestanas={VISTAS}
        activa={vista}
        alElegir={setVista}
        etiqueta="Facturas de generadores"
      />

      {vista === 'sire' ? (
        <>
          <p className="mb-3 text-[11px] uppercase leading-relaxed text-armazon-500">
            Facturas de servicios especiales que emitieron el SIRE (hasta julio de 2026) y el
            sistema anterior (agosto y septiembre) y quedaron sin pagar, con el saldo que tenían en
            el corte. Se cobran en la ventanilla como cualquier otra.
          </p>
          <FacturasPorOrigen
            origen="SIRE"
            situacionInicial=""
            vacio="No hay cartera anterior cargada"
          />
        </>
      ) : !ciclo ? (
        <div className="rounded-2xl border border-dashed border-armazon-300 bg-white p-12 text-center">
          <Receipt className="mx-auto size-10 text-armazon-200" aria-hidden />
          <p className="mt-3 text-sm font-semibold uppercase text-armazon-600">
            No hay ningún ciclo cerrado
          </p>
          <p className="mt-1 text-xs uppercase text-armazon-400">
            Se factura desde aquí el mes que se cerró en Control de pesaje
          </p>
        </div>
      ) : ciclo.estado === 'CERRADO' ? (
        <>
          <div className="mb-4 rounded-2xl border border-marca-200 bg-marca-50 p-5">
            <p className="text-sm font-bold uppercase text-marca-900">
              {enMes(ciclo.periodo)} está cerrado: se revisa, se le da el visto bueno y se factura
            </p>
            <div className="mt-3">
              <AvisoDelPlazo periodo={ciclo.periodo} />
            </div>
          </div>
          <EmisionSupervisada key={ciclo.id} ciclo={ciclo} alCambiar={refrescar} />
        </>
      ) : (
        <>
          {ciclo.abiertoPor === 'migracion' && (
            <p className="mb-4 rounded-xl border border-armazon-200 bg-white p-4 text-xs uppercase leading-relaxed text-armazon-600">
              {enMes(ciclo.periodo)} se facturó fuera de SIREPAGAL —en el SIRE o en el sistema
              anterior— y llegó con la migración: sus pesajes están en Control de pesaje como
              historial. Lo que quedó sin cobrar está en la pestaña «Cartera anterior».
            </p>
          )}
          {refacturable && (
            <div
              role="status"
              className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4"
            >
              <Undo2 className="mt-0.5 size-5 shrink-0 text-amber-600" aria-hidden />
              <p className="text-xs uppercase leading-relaxed text-amber-900">
                <strong>
                  {ciclo.sinFacturar === 1
                    ? '1 pesaje liberado'
                    : `${ciclo.sinFacturar} pesajes liberados`}
                </strong>{' '}
                por una nota de crédito o una anulación. Si hay que corregirlos, hágalo antes en
                Control de pesaje; al refacturar sale una factura nueva por generador, solo con
                ellos.
              </p>
            </div>
          )}
          {refacturable && (
            <EmisionSupervisada key={ciclo.id} ciclo={ciclo} alCambiar={refrescar} />
          )}
          <FacturasPorOrigen
            key={ciclo.periodo}
            origen="GENERADORES"
            periodo={ciclo.periodo}
            situacionInicial=""
            vacio="Ninguna factura de este mes coincide"
          />
        </>
      )}

      {confirmando && ciclo && (
        <Modal
          abierto
          alCerrar={() => setConfirmando(false)}
          titulo={
            refacturable
              ? `Refacturar lo liberado de ${enMes(ciclo.periodo)}`
              : `Facturar ${enMes(ciclo.periodo)}`
          }
          subtitulo="Una factura por generador, con una línea por pesaje"
          icono={refacturable ? Undo2 : Receipt}
          ancho="medio"
        >
          <div className="grid gap-4">
            {refacturable && (
              <div className="grid grid-cols-1 gap-3 text-center">
                <Dato valor={String(ciclo.sinFacturar)} rotulo="Pesajes liberados" />
              </div>
            )}
            {!refacturable && t && (
              <div className="grid grid-cols-3 gap-3 text-center">
                <Dato valor={String(t.generadores)} rotulo="Facturas" />
                <Dato valor={String(t.pesajes)} rotulo="Pesajes" />
                <Dato valor={`$${t.total.toFixed(2)}`} rotulo="Total" />
              </div>
            )}
            <AvisoDelPlazo periodo={ciclo.periodo} />
            {ciclo.revisadoPor && (
              <p className="text-[11px] font-semibold uppercase text-emerald-800">
                Con el visto bueno de {ciclo.revisadoPor}
              </p>
            )}
            <p className="text-[11px] uppercase leading-relaxed text-armazon-500">
              Las facturas nacen pendientes de cobro, vencen según el plazo de pago configurado y
              salen al SRI con la emisión masiva. Facturado, el ciclo ya no se reabre.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="h-10 rounded-lg border border-armazon-200 px-4 text-xs font-semibold uppercase text-armazon-600 hover:bg-armazon-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => facturar.mutate()}
                disabled={facturar.isPending}
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-5 text-xs font-bold uppercase text-white shadow-sm hover:bg-marca-700 disabled:opacity-50"
              >
                {facturar.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                Facturar
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

function Dato({ valor, rotulo }: { valor: string; rotulo: string }) {
  return (
    <div className="rounded-xl border border-armazon-200 bg-white p-3">
      <span className="importe block text-lg font-bold text-armazon-800">{valor}</span>
      <span className="text-[10px] font-semibold uppercase text-armazon-500">{rotulo}</span>
    </div>
  );
}

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

function enMes(periodo: string): string {
  const [anio, mes] = periodo.split('-');
  return `${MESES[Number(mes) - 1] ?? mes} ${anio}`;
}
